import Vue from 'vue'
import VueRouter from 'vue-router'
import { AppName } from '@/helpers/constants'
import Workspace from '@/views/Workspace'
import Home from '@/views/Home'
import KudosLibrary from '@/views/KudosLibrary'
import KudosRecognition from '@/views/KudosRecognition'
import Inbox from '@/views/Inbox'
import UserProfile from '@/views/UserProfile'
import TeamProfile from '@/views/TeamProfile'
import Dashboard from '@/views/Dashboard/Dashboard'
import DashboardChooseTeam from '@/views/Dashboard/ChooseTeam'
import DashboardOverview from '@/views/Dashboard/Overview'
import DashboardTeamPerformance from '@/views/Dashboard/TeamPerformance'
import DashboardTeamInteraction from '@/views/Dashboard/TeamInteraction'
import Settings from '@/views/Settings'
import InvitationLink from '@/views/InvitationLink'

import SignUpFlow from '@/views/SignUpFlow'
import CreateWorkspace from '@/views/signup/CreateWorkspace'
import CheckEmail from '@/views/signup/CheckEmail'
import CreateManager from '@/views/signup/CreateManager'
import CreateUser from '@/views/signup/CreateUser'

import ConnectToWorkspace from '@/views/signin/ConnectToWorkspace'
import Login from '@/views/signin/Login'
import ForgotWorkspace from '@/views/signin/ForgotWorkspace'

Vue.use(VueRouter)

function redirectToWithCompanySlugOrLogin(api, redirectToName) {
  const slug = api.getWorkspaceSlug()
  return slug ? { name: redirectToName, params: { companySlug: slug } } : { name: 'Login' }
}

export default api => {
  const routes = [
    {
      path: '/',
      name: 'Home',
      redirect: to => redirectToWithCompanySlugOrLogin(api, 'WorkspaceHome')
    },
    {
      path: '/settings',
      redirect: to => redirectToWithCompanySlugOrLogin(api, 'Settings')
    },
    {
      path: '/login/google/:token',
      redirect: to => {
        api.setToken(to.params.token)
        return { name: 'Home' }
      }
    },
    {
      path: '/accounts/login',
      component: SignUpFlow,
      meta: { requiresAuth: false },
      children: [
        {
          path: '',
          name: 'Login',
          component: Login
        },
        {
          path: 'workspace',
          name: 'ConnectToWorkspace',
          component: ConnectToWorkspace,
          meta: { title: 'Connect to your workspace' }
        },
        {
          path: 'forgot-workspace',
          name: 'ForgotWorkspace',
          component: ForgotWorkspace
        }
      ]
    },
    {
      path: '/signup',
      component: SignUpFlow,
      meta: { requiresAuth: false },
      children: [
        {
          path: 'company',
          // alias: '',
          name: 'CreateWorkspace',
          component: CreateWorkspace,
          meta: { title: 'Create a workspace' }
        },
        {
          path: 'checkemail',
          name: 'CheckEmail',
          component: CheckEmail,
          meta: { title: 'Check your email' }
        },
        {
          path: 'manager/google/:companySlug/:token',
          redirect: to => {
            api.setWorkspaceSlug(to.params.companySlug)
            api.setToken(to.params.token)
            return { name: 'CreateManager', params: { from: 'google' } }
          }
        },
        {
          path: 'manager/:verifyKey',
          redirect: to => {
            return { name: 'CreateManager', params: { verifyKey: to.params.verifyKey } }
          }
        },
        {
          path: 'manager',
          name: 'CreateManager',
          props: true,
          component: CreateManager
        },
        {
          path: 'user/google/:token',
          redirect: to => {
            api.setWorkspaceSlug(to.params.companySlug)
            api.setToken(to.params.token)
            return { name: 'CreateUser', params: { from: 'google' } }
          }
        },
        {
          path: 'user/:verifyKey',
          redirect: to => {
            return { name: 'CreateUser', params: { verifyKey: to.params.verifyKey } }
          }
        },
        {
          path: 'user',
          name: 'CreateUser',
          props: true,
          component: CreateUser
        }
      ]
    },
    {
      path: '/:companySlug/kudos/recognition/:key/give',
      name: 'KudosRecognition',
      component: KudosRecognition,
      meta: { requiresAuth: false }
    },
    {
      path: '/:companySlug/invitation-link/:key',
      name: 'InvitationLink',
      component: InvitationLink,
      meta: { requiresAuth: false }
    },
    {
      path: '/:companySlug',
      component: Workspace,
      meta: { title: 'Team Engagement and Inclusion' },
      children: [
        {
          path: '',
          name: 'WorkspaceHome',
          component: Home
        },
        {
          path: 'kudos',
          name: 'KudosLibrary',
          component: KudosLibrary
        },
        {
          path: 'messaging/inbox',
          name: 'Inbox',
          component: Inbox
        },
        {
          path: 'users/:userId',
          name: 'UserProfile',
          component: UserProfile
        },
        {
          path: 'team/:teamId',
          name: 'Team',
          component: TeamProfile
        },
        {
          path: 'dashboard',
          component: Dashboard,
          children: [
            {
              path: '',
              name: 'Dashboard',
              component: DashboardChooseTeam
            },
            {
              path: ':teamId/overview',
              name: 'DashboardOverview',
              component: DashboardOverview
            },
            {
              path: ':teamId/team-performance',
              name: 'DashboardTeamPerformance',
              component: DashboardTeamPerformance
            },
            {
              path: ':teamId/team-interaction',
              name: 'DashboardTeamInteraction',
              component: DashboardTeamInteraction
            }
          ]
        },
        {
          path: 'settings',
          name: 'Settings',
          component: Settings
        }
      ]
    }
  ]

  const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
  })

  router.beforeEach((to, from, next) => {
    const routes = to.matched.slice().reverse()

    const nearestWithRequiresAuth = routes.find(route => route.meta && (typeof route.meta.requiresAuth === typeof Boolean()))
    if (!nearestWithRequiresAuth || nearestWithRequiresAuth.meta.requiresAuth === true) {
      if (!api.getToken()) {
        next({
          name: 'Login',
          query: { redirect: to.fullPath }
        })
        return
      }
    }

    const nearestWithTitle = routes.find(route => route.meta && route.meta.title)
    if (nearestWithTitle) {
      document.title = `${AppName} | ${nearestWithTitle.meta.title}`
    }
    next()
  })

  return router
}
